<template>
  <div class="home-page">
    <div class="home-bu-bar">
      <span class="home-bu-bar__label">{{ t('buLabel') }}</span>
      <div
        class="home-bu-bar__chips"
        role="group"
        :aria-label="t('buLabel')"
      >
        <button
          v-for="item in buFilters"
          :key="item.key"
          type="button"
          class="home-bu-bar__chip"
          :class="{ 'home-bu-bar__chip--active': buFilter === item.key }"
          @click="buFilter = item.key"
        >
          {{ item.label }}
        </button>
      </div>
    </div>

    <section class="home-pipeline">
      <button
        type="button"
        class="home-pipeline__toggle"
        :aria-expanded="pipelineOpen"
        @click="togglePipeline"
      >
        <i
          class="bi bi-graph-up-arrow"
          aria-hidden="true"
        />
        {{ t('pipeline') }}
      </button>

      <div
        v-show="pipelineOpen"
        class="home-pipeline__panel"
      >
        <div class="home-pipeline__range">
          <button
            v-for="y in pipelineRanges"
            :key="y"
            type="button"
            class="home-bu-bar__chip"
            :class="{ 'home-bu-bar__chip--active': pipelineRange === y }"
            @click="pipelineRange = y"
          >
            {{ y === 1 ? t('pipelineYear') : t('pipelineYears', { n: y }) }}
          </button>
        </div>
        <HomeAnnualPipeline
          :range-years="pipelineRange"
          :bu="buFilter"
          :search="effectiveSearch"
          :selected-id="selectedProductId"
          @select="onProductSelect"
        />
      </div>
    </section>

    <HomeKanbanBoard
      ref="kanbanRef"
      :search="effectiveSearch"
      :bu="buFilter"
      :selected-id="selectedProductId"
      :reset-token="filterResetToken"
      :refresh-token="kanbanRefreshToken"
      @select="onProductSelect"
      @total="onKanbanTotal"
    />

    <div class="home-fab">
      <Tooltip
        :text="t('fab.scrollTop')"
        placement="top"
        :hide-delay="200"
      >
        <button
          type="button"
          class="home-fab__btn"
          @click="scrollTop"
        >
          <i
            class="bi bi-arrow-up"
            aria-hidden="true"
          />
        </button>
      </Tooltip>
      <Tooltip
        :text="t('fab.reset')"
        placement="top"
        :hide-delay="200"
      >
        <button
          type="button"
          class="home-fab__btn"
          @click="resetFilters"
        >
          <i
            class="bi bi-arrow-counterclockwise"
            aria-hidden="true"
          />
        </button>
      </Tooltip>
      <Tooltip
        :text="t('fab.reports')"
        placement="top"
        :hide-delay="200"
      >
        <button
          type="button"
          class="home-fab__btn"
          @click="openReportsModal"
        >
          <i
            class="bi bi-file-earmark-bar-graph-fill"
            aria-hidden="true"
          />
        </button>
      </Tooltip>
    </div>

    <Teleport to="body">
      <div
        v-if="reportsModalOpen"
        class="home-reports-modal"
        :class="{ 'home-reports-modal--viewer-open': reportsViewerOpen }"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-reports-modal-title"
        @click.self="closeReportsModal"
      >
        <div class="home-reports-modal__card">
          <div class="home-reports-modal__header">
            <h2 id="home-reports-modal-title">
              {{ t('fab.reportsTitle') }}
            </h2>
            <button
              type="button"
              class="home-reports-modal__close"
              :aria-label="t('fab.close')"
              @click="closeReportsModal"
            >
              <i
                class="bi bi-x-lg"
                aria-hidden="true"
              />
            </button>
          </div>
          <div class="home-reports-modal__body">
            <HomeAnalyticsPanel
              hide-title
              @report-viewer-open="reportsViewerOpen = true"
              @report-viewer-close="reportsViewerOpen = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="selectedProductId"
        class="home-lp-modal"
        role="dialog"
        aria-modal="true"
        @click.self="clearProductCard"
      >
        <div class="home-lp-modal__card">
          <HomeProductCard
            :id="selectedProductId"
            :context="selectedContext"
            class="home-lp-modal__body"
            @close="clearProductCard"
            @open-rsd="openRsdModal"
            @saved="onProductCardSaved"
          />
        </div>
      </div>
    </Teleport>

    <RsdModal
      v-if="selectedRsdId"
      :id="selectedRsdId"
      @close="selectedRsdId = null"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { watchDebounced } from '@vueuse/core'
import Tooltip from '@/components/tooltip.vue'
import HomeProductCard from '@/views/home/HomeProductCard.vue'
import HomeAnnualPipeline from '@/views/home/HomeAnnualPipeline.vue'
import HomeAnalyticsPanel from '@/views/home/HomeAnalyticsPanel.vue'
import HomeKanbanBoard from '@/views/home/kanban/HomeKanbanBoard.vue'
import RsdModal from '@/views/rsd/RsdModal.vue'
import { HOME_BU_FILTERS } from '@/views/home/kanban/columns'
import { CARD_CATEGORIES } from '@/lib/productCard'
import { useUiStore } from '@/stores/ui'
import { useMessageStack } from '@/stores/messagestack'
import { lockPageScroll, unlockPageScroll } from '@/lib/pageScrollLock'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const uiStore = useUiStore()
const { homeSearch } = storeToRefs(uiStore)
const { push: pushMessage } = useMessageStack()

const effectiveSearch = ref('')
const selectedProductId = ref(null)
const selectedContext = ref('planned_maa')
const selectedRsdId = ref(null)
const buFilter = ref('all')
const buFilters = HOME_BU_FILTERS
const pipelineOpen = ref(false)
/** По умолчанию — только текущий год; чипы 3/5/10 расширяют горизонт. */
const pipelineRange = ref(1)
const pipelineRanges = [1, 3, 5, 10]
const filterResetToken = ref(0)
const kanbanRefreshToken = ref(0)
const kanbanRef = ref(null)
const reportsModalOpen = ref(false)
const reportsViewerOpen = ref(false)
/** После сохранения карточки с Full Access — обновить канбан при закрытии модалки. */
const kanbanRefreshPending = ref(false)

function openRsdModal(rsdId) {
  const id = Number(rsdId)
  if (Number.isFinite(id) && id > 0) {
    selectedRsdId.value = id
  }
}

watchDebounced(
  homeSearch,
  value => {
    effectiveSearch.value = String(value || '').trim()
  },
  { debounce: 300, immediate: true }
)

function restoreSelectedFromRoute() {
  const product = Number(route.query.product)
  if (Number.isFinite(product) && product > 0) {
    selectedProductId.value = product
    const context = String(route.query.context || '')
    selectedContext.value = CARD_CATEGORIES[context] ? context : 'planned_maa'
  }
}

onMounted(restoreSelectedFromRoute)
watch(
  () => [route.query.product, route.query.context],
  () => restoreSelectedFromRoute()
)

watch(
  selectedProductId,
  product => {
    if (product) {
      lockPageScroll()
      document.body.classList.add('home-lp-modal-open')
    } else {
      document.body.classList.remove('home-lp-modal-open')
      unlockPageScroll()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.body.classList.remove('home-lp-modal-open')
  if (selectedProductId.value || reportsModalOpen.value) {
    unlockPageScroll()
  }
})

function onKanbanTotal() {
  /* counts live in board */
}

function onProductSelect({ id, category }) {
  selectedProductId.value = Number(id)
  selectedContext.value = category || 'planned_maa'
}

function onProductCardSaved() {
  kanbanRefreshPending.value = true
}

function clearProductCard() {
  selectedProductId.value = null
  if (kanbanRefreshPending.value) {
    kanbanRefreshPending.value = false
    kanbanRefreshToken.value += 1
  }
  if (route.query.product || route.query.context) {
    const query = { ...route.query }
    delete query.product
    delete query.context
    delete query.from
    router.replace({ name: 'home', query })
  }
}

function scrollTop() {
  const scroller = document.querySelector('.signed-layout--full-height > .container-main')
  if (scroller) {
    scroller.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function togglePipeline() {
  pipelineOpen.value = !pipelineOpen.value
}

function openReportsModal() {
  if (reportsModalOpen.value) {
    return
  }
  reportsModalOpen.value = true
  lockPageScroll()
}

function closeReportsModal() {
  if (!reportsModalOpen.value) {
    return
  }
  reportsModalOpen.value = false
  reportsViewerOpen.value = false
  unlockPageScroll()
}

function resetFilters() {
  buFilter.value = 'all'
  homeSearch.value = ''
  effectiveSearch.value = ''
  pipelineOpen.value = false
  pipelineRange.value = 1
  closeReportsModal()
  clearProductCard()
  filterResetToken.value += 1
  pushMessage({ type: 'info', message: t('filtersReset') })
}
</script>

<i18n>
{
  "ru": {
    "buLabel": "Business Unit:",
    "pipeline": "Pipeline",
    "pipelineYear": "Current year",
    "pipelineYears": "{n} years",
    "filtersReset": "Фильтры сброшены",
    "fab": {
      "scrollTop": "Scroll to top",
      "reset": "Reset all filters",
      "reports": "Reports",
      "reportsTitle": "Analytical Reports",
      "close": "Close"
    }
  },
  "en": {
    "buLabel": "Business Unit:",
    "pipeline": "Pipeline",
    "pipelineYear": "Current year",
    "pipelineYears": "{n} years",
    "filtersReset": "Filters reset",
    "fab": {
      "scrollTop": "Scroll to top",
      "reset": "Reset all filters",
      "reports": "Reports",
      "reportsTitle": "Analytical Reports",
      "close": "Close"
    }
  }
}
</i18n>
